<template>
	<div>
		<div class="-mt-8 mb-10 text-sm leading-6 text-gray-500 dark:text-gray-400">
			<p>
				You used an <span class="font-semibold dark:text-gray-300">expired</span> or <span class="font-semibold dark:text-gray-300">temporary</span> password.
			</p>
			<p>
				Please update your password below before continuing.
			</p>
		</div>
		<form
			@submit.prevent="updatePassword()"
			class="space-y-6">
			<div>
				<tw-input
					v-model="email"
					id="email"
					:required="true"
					type="email"
					autocomplete="email"
					label="Email address"
					validation="Please enter your email address." />
			</div>
			<div>
				<tw-input
					v-model="currentPassword"
					id="currentPassword"
					ref="inputPassword"
					:required="true"
					type="password"
					label="Current password"
					validation="Please fill in your current password." />
			</div>

			<fieldset class="mt-6 bg-white dark:bg-gray-900">
				<legend class="block text-sm font-bold leading-6 text-gray-900 dark:text-gray-200">
					New Password
				</legend>
				<div class="mt-2 -space-y-px rounded-md shadow-sm">
					<div>
						<label
							for="password"
							class="sr-only">New password</label>
						<div class="relative">
							<input
								v-model="newPassword"
								:type="showPassword ? 'text' : 'password'"
								name="new-password"
								id="password"
								autocomplete="new-password"
								class="pr-12 relative min-w-0 block w-full rounded-none rounded-t-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-dbBlue sm:text-sm sm:leading-6 dark:bg-white/5 dark:text-white dark:ring-white/10 dark:focus:ring-indigo-500"
								placeholder="New password">
							<div class="flex absolute inset-y-0 right-0">
								<button
									id="show-hide-password"
									tabindex="-1"
									class="pr-3 flex items-center text-gray-300 dark:text-gray-500 z-20"
									@click.prevent="showPassword = !showPassword">
									<EyeIconMini
										v-show="!showPassword"
										class="h-5 w-5"
										aria-hidden="true" 
										tabindex="-1" />
									<EyeSlashIconMini
										v-show="showPassword"
										class="h-5 w-5"
										aria-hidden="true" 
										tabindex="-1" />
								</button>
							</div>
						</div>
					</div>
					<div>
						<label
							for="confirm-password"
							class="sr-only">Confirm new password</label>
						<div class="relative">
							<input
								v-model="confirmNewPassword"
								:type="showPassword ? 'text' : 'password'"
								name="confirm-password"
								id="confirm-password"
								autocomplete="new-password"
								class="pr-12 relative min-w-0 block w-full rounded-none rounded-b-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-dbBlue sm:text-sm sm:leading-6 dark:bg-white/5 dark:text-white dark:ring-white/10 dark:focus:ring-indigo-500"
								placeholder="Confirm new password">
							<div class="flex absolute inset-y-0 right-0">
								<div
									id="password-match-indicator"
									class="pr-3 flex items-center pointer-events-none relative z-20">
									<CheckCircleIconMini
										:class="{'opacity-0': !passwordsMatch}"
										class="h-5 w-5 text-green-500 transition-all duration-300 ease-in-out absolute right-3"
										aria-hidden="true" />
									<CheckCircleIcon
										:class="{'opacity-0': passwordsMatch}"
										class="h-5 w-5 text-gray-300 dark:text-gray-500 transition-all duration-300 ease-in-out absolute right-3"
										aria-hidden="true" />
								</div>
							</div>
						</div>
					</div>
				</div>
			</fieldset>

			<div>
				<tw-button 
					type="submit" 
					class="btn-db-yellow w-full"
					:disabled="loading"
					:loading="loading">
					Continue
				</tw-button>
			</div>
		</form>
	</div>
</template>

<script lang="ts">
	export default { name: 'PasswordExpiredForm' } // name the component
</script>

<script setup lang="ts">
	// api
	import { 
		Auth
	} from 'aws-amplify'

	// components
	import {
		CheckCircleIcon
	} from '@heroicons/vue/24/outline'

	import {
		CheckCircleIcon as CheckCircleIconMini,
		EyeIcon as EyeIconMini,
		EyeSlashIcon as EyeSlashIconMini
	} from '@heroicons/vue/20/solid'

	// router
	import {
		useRouter
	} from 'vue-router'

	// store
	import { 
		store as _systemStore
	} from '@/store/system'

	import { 
		store as _userStore
	} from '@/store/user'

	// vue
	import {
		computed,
		ref
	} from 'vue'

	const router = useRouter()

	const userStore = _userStore()
	const systemStore = _systemStore()
	
	const email = ref("")
	const currentPassword = ref("")
	const newPassword = ref("")
	const confirmNewPassword = ref("")
	const loading = ref(false)
	const inputEmail = ref<null | {valid: Boolean}>(null)
	const inputPassword = ref<null | {valid: Boolean}>(null)
	const inputNewPassword = ref<null | {valid: Boolean}>(null)
	const inputNewPasswordConfirm = ref<null | {valid: Boolean}>(null)
	const passwordConfirmMessage = ref("Please fill in your new password.")

	const showPassword = ref<boolean>(false)

	const passwordsMatch = computed(() => {
		return newPassword.value && (newPassword.value === confirmNewPassword.value)
	})

	async function updatePassword() {
		loading.value = true
		let formError = false
		if (newPassword.value != confirmNewPassword.value) {
			document.getElementById('verifyNewPassword')?.focus()
			passwordConfirmMessage.value = "New password does not match."
			if(inputNewPasswordConfirm.value)inputNewPasswordConfirm.value.valid = false
			formError = true
		}
		if (confirmNewPassword.value == "") {
			document.getElementById('verifyNewPassword')?.focus()
			if(inputNewPasswordConfirm.value)inputNewPasswordConfirm.value.valid = false
			formError = true
		}
		if (newPassword.value == "") {
			document.getElementById('newPassword')?.focus()
			if(inputNewPassword.value)inputNewPassword.value.valid = false
			formError = true
		}
		if (currentPassword.value == "") {
			document.getElementById('currentPassword')?.focus()
			if(inputPassword.value)inputPassword.value.valid = false
			formError = true
		}
		if (email.value == "") {
			document.getElementById('email')?.focus()
			if(inputEmail.value)inputEmail.value.valid = false
			formError = true
		}
		
		if(formError) {
			loading.value = false
			return
		}

		try {
			await Auth.completeNewPassword(userStore.user.cognitoUser, newPassword.value)
			const user = await Auth.signIn(email.value, newPassword.value)
			userStore.user.cognitoUser = user
			loading.value = false
			router.push({name: systemStore.system.primaryRoute})
		} 
		catch(e) {
			console.error(e)
			// do something with the error
		}
		finally {
			loading.value = false
		}
	}
</script>
